import { useState } from "react";
import { motion } from "framer-motion";
import { Search, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeading, reveal } from "@/components/site/primitives";
import Seo from "@/components/site/Seo";
import { CTABand } from "@/pages/BusinessWaste";
import { citySlug } from "@/pages/CityLanding";
import CoverageMap from "@/components/site/CoverageMap";
import { CITIES, CITY_COORDS, IMAGES } from "@/lib/data";
import { RICH_CITY_SLUGS, CITY_CONTENT } from "@/lib/cityContent";
import { useI18n } from "@/i18n";

const featured = RICH_CITY_SLUGS
  .map((s) => {
    const c = CITY_COORDS.find((cc) => citySlug(cc.name) === s);
    if (!c) return null;
    return { name: c.name, slug: s, blurb: CITY_CONTENT[s].intro.split(". ")[0] + "." };
  })
  .filter(Boolean);

export default function ServiceAreas() {
  const { t, localePath } = useI18n();
  const [query, setQuery] = useState("");
  const [focus, setFocus] = useState(null);
  const filtered = CITIES.filter((c) => c.toLowerCase().includes(query.toLowerCase()));
  const coordFor = (name) => CITY_COORDS.find((c) => c.name === name);
  const mono = "font-mono-tech uppercase tracking-[0.18em]";

  return (
    <>
      <Seo
        title={t("sa.seoTitle")}
        description={t("sa.seoDesc")}
        path="/service-areas"
        image={IMAGES.rolloffDumpster}
      />

      <PageHero
        crumb={t("sa.crumb")}
        eyebrow={`${CITIES.length} ${t("sa.communities")}`}
        title={t("sa.title")}
        highlight={t("sa.highlight")}
        subtitle={t("sa.subtitle")}
      />

      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.08fr_0.92fr] gap-8 items-start">
          <motion.div className="min-w-0" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <CoverageMap focus={focus} />
            <p className={`mt-4 border-l-2 border-emerald-600 pl-3 ${mono} text-[10px] text-slate-500`} data-testid="coverage-map-caption">
              {t("sa.interactive")}
            </p>
          </motion.div>

          <div className="min-w-0 space-y-5 lg:sticky lg:top-28" data-testid="area-index-panel">
            <div className="border-2 border-[#0B0F14] bg-[#F0EFEA] p-5 shadow-[3px_3px_0_#0E3B3B] sm:shadow-[5px_5px_0_#0E3B3B] sm:p-6" data-testid="area-glance">
              <p className={`${mono} text-[10px] font-bold text-slate-500`}>{t("sa.glance")}</p>
              <div className="mt-4 flex gap-9">
                <div>
                  <div className="font-display text-5xl font-extrabold leading-none text-emerald-600 sm:text-6xl">{CITIES.length}</div>
                  <p className={`${mono} mt-2 text-[10px] text-[#0B0F14]`}>{t("sa.citiesServed")}</p>
                </div>
                <div>
                  <div className="font-display text-5xl font-extrabold leading-none text-[#0E3B3B] sm:text-6xl">1</div>
                  <p className={`${mono} mt-2 text-[10px] text-[#0B0F14]`}>{t("sa.countyServed")}</p>
                </div>
              </div>
              <p className={`${mono} mt-5 max-w-md text-[10px] leading-relaxed text-slate-600`}>{t("sa.glanceBody")}</p>
            </div>

            <div className="relative">
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("sa.searchPlaceholder")}
                data-testid="area-search"
                className={`w-full border-2 border-[#0B0F14] bg-[#F0EFEA] py-3.5 pl-11 pr-4 text-[11px] text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 ${mono}`}
              />
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid="coverage-legend">
              <span className={`inline-flex items-center gap-2 ${mono} text-[9px] text-slate-600`}>
                <span className="h-3 w-3 bg-emerald-600" /> {t("sa.legendService")}
              </span>
              <span className={`inline-flex items-center gap-2 ${mono} text-[9px] text-slate-600`}>
                <span className="h-3 w-3 bg-amber-500" /> {t("sa.legendSelected")}
              </span>
              <span className={`inline-flex items-center gap-2 ${mono} text-[9px] text-slate-600`}>
                <span className="h-3 w-3 border-2 border-[#0E3B3B] bg-transparent" /> {t("sa.legendRadius")}
              </span>
            </div>

            <div className="border-2 border-[#0B0F14] bg-white shadow-[3px_3px_0_#0E3B3B] sm:shadow-[5px_5px_0_#0E3B3B]" data-testid="city-index">
              <div className="flex items-center justify-between bg-[#0E3B3B] px-3 py-2.5 text-white sm:px-4">
                <span className={`${mono} text-[10px] font-bold`}>{t("sa.cityIndex")}</span>
                <span className={`${mono} text-[9px] text-white/60`}>{t("sa.cityIndexSub")}</span>
              </div>
              <p className={`border-b border-[#D8D5CA] bg-[#FBFAF8] px-3 py-2 ${mono} text-[9px] text-slate-500`}>
                {filtered.length} / {CITIES.length} {t("sa.areasWord")}
              </p>
              {filtered.length === 0 ? (
                <p className="p-6 text-sm text-slate-500" data-testid="area-no-match">{t("sa.noMatch")}</p>
              ) : (
                <div className="grid max-h-[430px] overflow-auto sm:grid-cols-2">
                  {filtered.map((city) => {
                    const active = focus && focus.name === city;
                    return (
                      <button
                        key={city}
                        type="button"
                        onClick={() => setFocus(coordFor(city))}
                        aria-pressed={active}
                        data-testid={`area-chip-${city.toLowerCase().replace(/\s/g, "-")}`}
                        className={`group flex items-center justify-between gap-3 border-b border-[#D8D5CA] px-3 py-3 text-left transition-colors sm:odd:border-r ${active ? "bg-[#0E3B3B] text-white" : "bg-white/70 text-[#0B0F14] hover:bg-emerald-50"}`}
                      >
                        <span className="min-w-0">
                          <span className={`block truncate font-mono-tech text-[11px] font-bold uppercase tracking-wider ${active ? "text-white" : "text-[#0B0F14]"}`}>{city}</span>
                          <span className={`block font-mono-tech text-[9px] uppercase tracking-widest ${active ? "text-emerald-300" : "text-slate-500"}`}>{t("sa.cityCounty")}</span>
                        </span>
                        <ArrowRight size={13} className={`shrink-0 transition-transform group-hover:translate-x-0.5 ${active ? "text-amber-400" : "text-emerald-600"}`} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured local guides — links to rich city pages */}
      <section className="relative bg-[#F0EFEA] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("sa.featuredEyebrow")} title={t("sa.featuredTitle")} highlight={t("sa.featuredHighlight")} className="mb-4 mx-auto" />
          <p className="max-w-2xl mx-auto text-center text-slate-600 mb-14">
            {t("sa.featuredIntro")}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((c, i) => (
              <motion.div key={c.slug} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <Link to={localePath(`/dumpster-rental/${c.slug}`)} data-testid={`featured-city-${c.slug}`}
                  className="group block h-full bg-white border border-[#E7E5DF] rounded-3xl p-7 shadow-soft hover:-translate-y-1 hover:border-emerald-600/40 transition-all">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-emerald-600/10 text-emerald-700 mb-4"><MapPin size={20} /></span>
                  <h3 className="font-display font-extrabold text-xl text-[#0B0F14]">{c.name}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed line-clamp-4">{c.blurb}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-semibold text-emerald-700 text-sm">
                    {t("sa.viewGuidePre")} {c.name} {t("sa.viewGuidePost")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All areas — crawlable internal links to every city page */}
      <section className="relative bg-[#0E3B3B] text-white py-24" data-testid="all-areas">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-emerald-400 font-semibold">{t("sa.allEyebrow")}</span>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05] max-w-3xl">
            {t("sa.allTitleA")} <span className="text-emerald-400">{t("sa.allTitleB")}</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl leading-relaxed">
            {t("sa.allBody")}
          </p>
          <div className="mt-10 flex flex-wrap gap-2.5">
            {CITIES.map((name) => (
              <Link key={name} to={localePath(`/dumpster-rental/${citySlug(name)}`)}
                className="px-4 py-2 rounded-full bg-white/[0.07] border border-white/15 text-sm text-white/90 hover:bg-emerald-500 hover:text-[#0A2C2C] hover:border-emerald-500 transition-colors"
                data-testid={`area-link-${citySlug(name)}`}>
                {name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
